import { LocationMapDialog } from '@/components/controls';
import { useInsertSubject, useSubjects, useToggleSubjectKey } from '@/hooks';
import { getGroupType } from '@/utils';
import { Done as DoneIcon } from '@mui/icons-material';
import { Masonry } from '@mui/lab';
import {
  alpha,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useMemo, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { HighPrioritySuspect } from './HighPrioritySuspect';
import { PrisonRecall } from './PrisonRecall';
import { Suspect } from './Suspect';
import { useNichePrisonRecalls } from './useNichePrisonRecalls';
import { useNicheSuspects } from './useNicheSuspects';
import { useNicheWarrants } from './useNicheWarrants';
import { Warrant } from './Warrant';

export function Subjects({ groupCodes, onComplete }) {
  const { data: suspects } = useNicheSuspects(groupCodes);
  const { data: prisonRecalls } = useNichePrisonRecalls(
    getGroupType('SECTOR'),
    groupCodes,
  );
  const { data: outstandingWarrants } = useNicheWarrants(
    getGroupType('SECTOR'),
    groupCodes,
  );
  const { data: highPrioritySuspects } = useSubjects('SUSPECT', groupCodes, {
    highPriority: true,
  });
  const outstandingSuspects = useMemo(
    () =>
      suspects.filter(
        (suspect) =>
          !(highPrioritySuspects ?? []).some(
            (highPrioritySuspect) =>
              highPrioritySuspect.recordId === suspect.id,
          ),
      ),
    [suspects, highPrioritySuspects],
  );
  const { mutate: insertSubject } = useInsertSubject();
  const { mutate: toggleSubjectKey } = useToggleSubjectKey();
  const { width, ref } = useResizeDetector();
  const highPriorityColumns = useMemo(
    () => Math.floor(width / 328) || 1,
    [width],
  );
  const lowPriorityColumns = useMemo(
    () => Math.floor(width / 248) || 1,
    [width],
  );
  const [selectedIndex, setSelectedIndex] = useState({
    type: null,
    recordId: null,
  });
  const selectedRecord = useMemo(() => {
    switch (selectedIndex.type) {
      case 'SUSPECT':
        return suspects.find(({ id }) => id === selectedIndex.recordId);
      case 'PRISON_RECALL':
        return prisonRecalls.find(({ id }) => id === selectedIndex.recordId);
      case 'WARRANT':
        return outstandingWarrants.find(
          ({ id }) => id === selectedIndex.recordId,
        );
      default:
        return null;
    }
  }, [selectedIndex, suspects, prisonRecalls, outstandingWarrants]);

  const handleShowMapClick = (type, id) => () => {
    setSelectedIndex(type, id);
  };

  function handleMapClose() {
    setSelectedIndex({
      type: null,
      recordId: null,
    });
  }

  function handleCompleteClick() {
    onComplete();
  }

  const handleOptionClick = (type, recordId, code, key) => () => {
    if (code) {
      toggleSubjectKey({ code, key });
    } else {
      insertSubject({ type, recordId, groupCodes, keys: [key] });
    }
  };

  return (
    <Box sx={{ px: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={(theme) => ({
          alignItems: 'center',
          position: 'sticky',
          top: 48,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          zIndex: 700,
          pl: 1,
          py: 1,
        })}
      >
        <Typography variant="h6">Suspects, Recalls & Warrants</Typography>
        <Box>
          <Tooltip title="Done" placement="right">
            <IconButton onClick={handleCompleteClick}>
              <DoneIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      </Stack>
      <Stack spacing={1} sx={{ pb: 1 }} ref={ref}>
        {highPrioritySuspects?.length > 0 && (
          <Fragment>
            <Typography variant="subtitle1" sx={{ pl: 1 }}>
              High Priority Suspects
            </Typography>
            <Masonry spacing={1} columns={highPriorityColumns}>
              {highPrioritySuspects.map(({ recordId }) => (
                <HighPrioritySuspect
                  key={recordId}
                  recordId={recordId}
                  onOptionClick={handleOptionClick}
                  onShowMapClick={handleShowMapClick({
                    type: 'SUSPECT',
                    recordId,
                  })}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
        {outstandingSuspects.length > 0 && (
          <Fragment>
            <Typography variant="subtitle1" sx={{ pl: 1 }}>
              Outstanding Suspects
            </Typography>
            <Masonry spacing={1} columns={lowPriorityColumns}>
              {outstandingSuspects.map((suspect) => (
                <Suspect
                  key={suspect.id}
                  suspect={suspect}
                  onOptionClick={handleOptionClick}
                  onShowMapClick={handleShowMapClick({
                    type: 'SUSPECT',
                    recordId: suspect.id,
                  })}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
        {prisonRecalls?.length > 0 && (
          <Fragment>
            <Typography variant="subtitle1" sx={{ pl: 1 }}>
              Prison Recalls
            </Typography>
            <Masonry spacing={1} columns={lowPriorityColumns}>
              {prisonRecalls.map((recall) => (
                <PrisonRecall
                  key={recall.id}
                  recall={recall}
                  onOptionClick={handleOptionClick}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
        {outstandingWarrants?.length > 0 && (
          <Fragment>
            <Typography variant="subtitle1" sx={{ pl: 1 }}>
              Outstanding Warrants
            </Typography>
            <Masonry spacing={1} columns={lowPriorityColumns}>
              {outstandingWarrants.map((warrant) => (
                <Warrant
                  key={warrant.id}
                  warrant={warrant}
                  onOptionClick={handleOptionClick}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
      </Stack>
      <LocationMapDialog
        point={selectedRecord?.addresses?.[0].point}
        title={`${selectedRecord?.forenames} ${selectedRecord?.surname} home address`}
        description={selectedRecord?.addresses?.[0]?.label}
        isOpen={!!selectedRecord}
        onClose={handleMapClose}
      />
    </Box>
  );
}
